import type {
  SiteBlueprint,
  SiteSectionItem,
} from '../types.js';

import type {
  BrandPersonality,
  BusinessArchetype,
  DesignBrief,
  MediaEmphasis,
  PrimaryGoal,
} from './design-brief.js';

const WELLNESS_TERMS = [
  'feng shui',
  'wellness',
  'wellbeing',
  'harmony',
  'balance',
  'healing',
  'holistic',
  'mindfulness',
  'energy',
  'spiritual',
  'meditation',
  'yoga',
  'spa',
];

const SERVICE_TERMS = [
  'service',
  'repair',
  'maintenance',
  'installation',
  'renovation',
  'contractor',
  'construction',
  'pool',
  'roofing',
  'plumbing',
  'electrician',
  'hvac',
  'landscaping',
  'cleaning',
];

const PROFESSIONAL_TERMS = [
  'attorney',
  'law firm',
  'accounting',
  'accountant',
  'financial',
  'advisory',
  'consulting',
  'consultant',
  'legal',
  'insurance',
  'real estate',
];

const CREATIVE_TERMS = [
  'photography',
  'photographer',
  'portfolio',
  'creative',
  'studio',
  'designer',
  'design',
  'architecture',
  'interior',
  'branding',
  'agency',
  'selected work',
];

const RETAIL_TERMS = [
  'shop',
  'store',
  'boutique',
  'collection',
  'products',
  'catalog',
  'order online',
  'buy',
];

function normalizeText(
  value: unknown,
): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function itemText(
  item: string | SiteSectionItem,
): string {
  if (typeof item === 'string') {
    return item;
  }

  return [
    item.title,
    item.text,
    item.body,
    item.description,
  ]
    .filter(Boolean)
    .join(' ');
}

function buildEvidenceText(
  blueprint: SiteBlueprint,
): string {
  const values: unknown[] = [
    blueprint.businessName,
    blueprint.title,
    blueprint.pageHeading,
    blueprint.description,

    ...blueprint.navigation.flatMap(
      (item) => [
        item.text,
        item.href,
      ],
    ),

    ...blueprint.sections.flatMap(
      (section) => [
        section.type,
        section.heading,
        ...section.paragraphs,
        ...section.items.map(itemText),
      ],
    ),
  ];

  return normalizeText(
    values
      .filter(Boolean)
      .join(' '),
  );
}

function scoreTerms(
  evidence: string,
  terms: string[],
): number {
  return terms.reduce(
    (score, term) =>
      evidence.includes(term)
        ? score + 1
        : score,
    0,
  );
}

function chooseBusinessArchetype(
  evidence: string,
): BusinessArchetype {
  const scores: Array<{
    archetype: BusinessArchetype;
    score: number;
  }> = [
    {
      archetype: 'wellness-consulting',
      score: scoreTerms(
        evidence,
        WELLNESS_TERMS,
      ),
    },
    {
      archetype: 'service-business',
      score: scoreTerms(
        evidence,
        SERVICE_TERMS,
      ),
    },
    {
      archetype: 'professional-services',
      score: scoreTerms(
        evidence,
        PROFESSIONAL_TERMS,
      ),
    },
    {
      archetype: 'creative-studio',
      score: scoreTerms(
        evidence,
        CREATIVE_TERMS,
      ),
    },
    {
      archetype: 'retail',
      score: scoreTerms(
        evidence,
        RETAIL_TERMS,
      ),
    },
  ];

  scores.sort(
    (left, right) =>
      right.score - left.score,
  );

  if ((scores[0]?.score ?? 0) === 0) {
    return 'unknown';
  }

  return scores[0].archetype;
}

function choosePrimaryGoal(
  evidence: string,
): PrimaryGoal {
  if (
    /\b(book|schedule|reserve)\b/.test(
      evidence,
    ) &&
    /\b(consultation|appointment|session)\b/.test(
      evidence,
    )
  ) {
    return 'book-consultation';
  }

  if (
    /\b(quote|estimate|proposal)\b/.test(
      evidence,
    )
  ) {
    return 'request-quote';
  }

  if (
    /\b(portfolio|gallery|projects?|selected work)\b/.test(
      evidence,
    )
  ) {
    return 'showcase-work';
  }

  if (
    /\b(blog|resources?|learn|education|guide)\b/.test(
      evidence,
    )
  ) {
    return 'educate';
  }

  if (
    /\b(testimonials?|reviews?|trusted|certified|experience)\b/.test(
      evidence,
    )
  ) {
    return 'build-trust';
  }

  return 'contact';
}

function chooseMediaEmphasis(
  blueprint: SiteBlueprint,
): MediaEmphasis {
  const sectionImageCount =
    blueprint.sections.reduce(
      (total, section) =>
        total + section.images.length,
      0,
    );

  const imageCount =
    new Set([
      ...blueprint.images.map(
        (image) => image.src,
      ),
      ...blueprint.sections.flatMap(
        (section) =>
          section.images.map(
            (image) => image.src,
          ),
      ),
    ]).size;

  const hasHeroMedia = Boolean(
    blueprint.heroMedia?.src,
  );

  if (
    imageCount >= 6 ||
    (hasHeroMedia && imageCount >= 3) ||
    sectionImageCount >= 5
  ) {
    return 'dominant';
  }

  if (
    hasHeroMedia ||
    imageCount >= 2 ||
    sectionImageCount >= 2
  ) {
    return 'supporting';
  }

  return 'minimal';
}

function choosePersonality(
  archetype: BusinessArchetype,
): BrandPersonality[] {
  switch (archetype) {
    case 'wellness-consulting':
      return [
        'calm',
        'premium',
      ];

    case 'creative-studio':
      return [
        'premium',
        'minimal',
      ];

    case 'retail':
      return [
        'friendly',
      ];

    case 'professional-services':
    case 'service-business':
    case 'unknown':
    default:
      return [
        'professional',
      ];
  }
}

export function buildDesignBrief(
  blueprint: SiteBlueprint,
): DesignBrief {
  const evidence =
    buildEvidenceText(blueprint);

  const businessArchetype =
    chooseBusinessArchetype(evidence);

  const primaryGoal =
    choosePrimaryGoal(evidence);

  const mediaEmphasis =
    chooseMediaEmphasis(blueprint);

  const visualDensity =
    businessArchetype ===
      'wellness-consulting' ||
    businessArchetype ===
      'creative-studio'
      ? 'airy'
      : 'balanced';

  const conversionEmphasis =
    primaryGoal === 'request-quote'
      ? 'strong'
      : businessArchetype ===
          'wellness-consulting'
        ? 'balanced'
        : primaryGoal === 'showcase-work'
          ? 'soft'
          : 'balanced';

  return {
    businessArchetype,

    personality:
      choosePersonality(
        businessArchetype,
      ),

    primaryGoal,

    visualDensity,

    mediaEmphasis,

    conversionEmphasis,

    reasons: [
      `Classified the business as ${businessArchetype}.`,
      `Selected ${primaryGoal} as the primary conversion goal.`,
      `Selected ${mediaEmphasis} media emphasis from the available image evidence.`,
      `Selected ${visualDensity} visual density for this business archetype.`,
    ],
  };
}
